<script lang="ts" setup>
    import { nextTick, onBeforeUnmount, useTemplateRef, watch } from 'vue'

    /**
     * Audio-reactive bars for a live voice session: the visible proof that the
     * microphone is open and that the agent is actually speaking.
     *
     * Fed by pull, not by push: the parent hands over samplers and this component
     * calls them once per animation frame. Sixty reactive writes a second through
     * a component tree would cost more than the animation itself, and the levels
     * are only ever needed here.
     */
    const props = withDefaults(
        defineProps<{
            /** Loudness of the channel currently on air, 0 to 1. */
            sample: () => number
            /** Frequency bins of the same channel, when the transport exposes them. */
            spectrum?: () => Uint8Array | undefined
            state: 'idle' | 'connecting' | 'listening' | 'speaking' | 'error'
            bars?: number
        }>(),
        { bars: 5, spectrum: undefined },
    )

    const barsEl = useTemplateRef<HTMLElement>('barsEl')

    const REST = 0.18
    const SMOOTHING = 0.35

    /**
     * Current heights, deliberately NOT reactive: they change every frame and are
     * written straight to a CSS custom property. A ref here would re-render the
     * bars sixty times a second to produce the same DOM.
     */
    let heights = Array.from({ length: props.bars }, () => REST)

    let frame: number | undefined

    const prefersReducedMotion = () =>
        typeof window !== 'undefined' &&
        window.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true

    /**
     * One target height per bar. With a spectrum, each bar owns a band, so the
     * shape follows the voice; without it, the single loudness value is spread
     * with a phase offset per bar so it still reads as speech and not as a
     * progress bar.
     */
    const readTargets = (): number[] => {
        const level = Math.min(1, Math.max(0, props.sample()))
        const bins = props.spectrum?.()
        const count = heights.length

        if (!bins?.length) {
            const now = Date.now() / 260
            return Array.from({ length: count }, (_unused, index) => {
                const wave = 0.65 + 0.35 * Math.sin(now + index * 0.9)
                return REST + level * 2.4 * wave
            })
        }

        // Speech lives in the lower half of the spectrum: using the whole range
        // would leave most bars flat.
        const usable = Math.max(1, Math.floor(bins.length / 2))
        const bandSize = Math.max(1, Math.floor(usable / count))
        return Array.from({ length: count }, (_unused, index) => {
            let total = 0
            for (let bin = 0; bin < bandSize; bin += 1) {
                total += bins[index * bandSize + bin] ?? 0
            }
            return REST + (total / bandSize / 255) * 2.2
        })
    }

    const render = () => {
        const nodes = barsEl.value?.children
        if (!nodes) {
            return
        }
        heights.forEach((height, index) => {
            ;(nodes[index] as HTMLElement | undefined)?.style.setProperty(
                '--pk-voice-orb-scale',
                String(height),
            )
        })
    }

    const paint = () => {
        const targets = readTargets()
        heights = heights.map((current, index) => {
            const target = Math.min(1, Math.max(REST, targets[index] ?? REST))
            return current + (target - current) * SMOOTHING
        })
        render()
        frame = requestAnimationFrame(paint)
    }

    const stopPainting = () => {
        if (frame !== undefined) {
            cancelAnimationFrame(frame)
            frame = undefined
        }
    }

    watch(
        () => props.state,
        async (state) => {
            const live = state === 'listening' || state === 'speaking'
            if (!live || prefersReducedMotion()) {
                stopPainting()
                heights = heights.map(() => REST * 1.6)
                // The bars may not be in the DOM yet on the first call.
                await nextTick()
                render()
                return
            }
            if (frame === undefined) {
                frame = requestAnimationFrame(paint)
            }
        },
        { immediate: true },
    )

    onBeforeUnmount(stopPainting)
</script>

<template>
    <div
        class="pk-voice-orb"
        :class="`pk-voice-orb--${state}`"
        aria-hidden="true">
        <div ref="barsEl" class="pk-voice-orb__bars">
            <span
                v-for="index in bars"
                :key="index"
                class="pk-voice-orb__bar" />
        </div>
    </div>
</template>

<style lang="scss">
    .pk-voice-orb {
        --pk-voice-orb-color: var(--color-word-3);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        // The size is a custom property so a host can scale the orb without
        // reaching into its internals, and its default lives here in the
        // fallback rather than as a declaration on this block: declared, it
        // would sit on the element itself and shadow whatever the host sets
        // on an ancestor.
        width: var(--pk-voice-orb-size, var(--spacing-40));
        height: var(--pk-voice-orb-size, var(--spacing-40));
        border-radius: var(--rounded-full);
        background-color: color-mix(
            in srgb,
            var(--pk-voice-orb-color) 12%,
            transparent
        );
        transition: background-color var(--duration-200, 200ms) ease;

        &__bars {
            display: flex;
            align-items: center;
            gap: 2px;
            height: var(--pk-voice-orb-bars-height, var(--spacing-20));
        }

        &__bar {
            width: 3px;
            height: 100%;
            border-radius: var(--rounded-full);
            background-color: var(--pk-voice-orb-color);
            transform: scaleY(var(--pk-voice-orb-scale, 0.2));
            transform-origin: center;
        }

        // The user is on air: the accent colour says "you are being heard".
        &--listening {
            --pk-voice-orb-color: var(--color-brand);
        }

        &--speaking {
            --pk-voice-orb-color: var(--color-accent);
        }

        // A live call breathes even in silence: the bars rest flat when nobody
        // talks, so without this ring an open line is indistinguishable from a
        // dead one.
        &--listening,
        &--speaking {
            animation: pk-voice-orb-live 2s ease-out infinite;
        }

        &--error {
            --pk-voice-orb-color: var(--color-danger);
        }

        &--connecting &__bar {
            animation: pk-voice-orb-wait 1.2s ease-in-out infinite;
        }

        &--connecting &__bar:nth-child(2) {
            animation-delay: 0.15s;
        }
        &--connecting &__bar:nth-child(3) {
            animation-delay: 0.3s;
        }
        &--connecting &__bar:nth-child(4) {
            animation-delay: 0.45s;
        }
        &--connecting &__bar:nth-child(5) {
            animation-delay: 0.6s;
        }
    }

    @keyframes pk-voice-orb-wait {
        0%,
        100% {
            transform: scaleY(0.25);
        }
        50% {
            transform: scaleY(0.8);
        }
    }

    // A sonar ping: the ring expands and fades, then the snap back to zero is
    // the start of the next ping rather than a visible jump.
    @keyframes pk-voice-orb-live {
        from {
            box-shadow: 0 0 0 0
                color-mix(in srgb, var(--pk-voice-orb-color) 35%, transparent);
        }
        to {
            box-shadow: 0 0 0 var(--spacing-8) transparent;
        }
    }

    @media (prefers-reduced-motion: reduce) {
        .pk-voice-orb__bar {
            animation: none;
            transform: scaleY(0.45);
        }

        // Still say "live", just without moving: a steady ring instead of the ping.
        .pk-voice-orb--listening,
        .pk-voice-orb--speaking {
            animation: none;
            box-shadow: 0 0 0 3px
                color-mix(in srgb, var(--pk-voice-orb-color) 20%, transparent);
        }
    }
</style>
